import swal from "sweetalert"

import constants from "shared/forms/BulkActions/constants"
import { stringContainsHTMLMarkup } from "shared/imports/sharedHelperFunctions"
import { TEXT_URL_DELIMITER } from "app/static/frontend/profiles-new/constants"

/**
 * This function will, depending on the bulkActionType, construct the args to be passed to the BulkAction
 * and fire the POST request that will create the action.
 *
 * @param {Immutable} values => the bulkActionsForm values from the redux store
 * @param {Object} props => the bulkActionsForm in app/static/frontend/search-new/containers/BulkActions/BulkActionsConsole
 * (in app/static/frontend/search-new/components/BulkActions/buttons/BulkActionsSubmitButton)
 * invoked from props.handleSubmit()
 */
export const onSubmit = async (values, dispatch, props) => {
    const onSubmitApprove = () => {
        const args = {}
        const approve = values.get("approve")

        if (approve) {
            args.approve = approve
        }

        return args
    }

    const onSubmitArchive = () => {
        const args = {}
        const archive = values.get("archive")

        if (archive) {
            args.archive = archive
        }

        return args
    }

    const onSubmitAlignment = () => {
        const args = {
            alignment_type: values.get("alignment_type"),
        }

        return args
    }

    const onSubmitAssignment = () => {
        const args = {}
        const assignmentInputType = values.get("assignment_input_type")
        const assignmentSelectType = values.get("assignment_select_type")
        const primary = values.get("primary")
        const supporters = values.get("supporters")
        const users = values.get("users")

        if (assignmentSelectType !== DjangIO.app.search.bulkactions.types.AssignmentSelectType.clear.value) {
            // only append the input type (user/supporter) if we are adding/removing
            args.assignment_input_type = assignmentInputType

            // only append supporters if submitting Contacts
            if (
                assignmentInputType === DjangIO.app.search.bulkactions.types.AssignmentInputType.supporter.value &&
                supporters
            ) {
                args.supporters = supporters && supporters.toJS()
            }

            // only append users if submitting Team Members
            if (assignmentInputType === DjangIO.app.search.bulkactions.types.AssignmentInputType.user.value && users) {
                if (primary) {
                    args.primary = primary
                }

                args.users = users && users.toJS()
            }
        }

        // submit the select type (add/remove/clear) every time
        args.assignment_select_type = assignmentSelectType

        return args
    }

    const onSubmitCustomField = () => {
        const args = {}
        const customFieldArgs = {}
        const customFieldSlug = values.get("custom_field_slug")
        const customFieldType = values.get("custom_field_type")

        const customFieldValue = values.get(customFieldSlug)
        // IE 9+ compatible way to check if a js object is an Array
        const isCustomFieldValueArray = Array.isArray(customFieldValue)

        const tagType = props.customFieldNameDict[customFieldSlug].tag_type
        customFieldArgs.tag_type = tagType
        customFieldArgs.slug = customFieldSlug

        if (
            // if the custom field value in the store is an array that is greater than 0
            (isCustomFieldValueArray && customFieldValue.length) ||
            // or if the custom field value in the store is a primitive that exists (and can also be false)
            (!isCustomFieldValueArray && (customFieldValue || customFieldValue === false))
        ) {
            customFieldArgs.value =
                tagType === DjangIO.app.person.types.TagType.number.value
                    ? parseInt(customFieldValue)
                    : customFieldValue
        }

        if (customFieldArgs) {
            args.custom_field = customFieldArgs
        }

        if (customFieldType) {
            args.custom_field_type = customFieldType
        }

        // based on parsing of text custom fields with external links used in
        // shared/customFields/action-creators.js
        if (tagType === DjangIO.app.person.types.TagType.string.value) {
            const urlInputValue = values.toJS()[`${customFieldSlug}_url`]
            if (urlInputValue) {
                const combinedDelimitedStringValue = `${customFieldValue}${TEXT_URL_DELIMITER}${urlInputValue}`
                args.custom_field.value = combinedDelimitedStringValue
            }
        }

        return args
    }

    const onSubmitExternalContact = () => {
        const args = {}
        const externalContact = values.get("external_contact")

        if (externalContact > 0) {
            args.external_contact = externalContact
        }

        return args
    }

    const onSubmitIssue = () => {
        const args = {}
        const issues = values.get("issues")
        const issueType = values.get("issue_type")
        const issueSelectTypeClear = DjangIO.app.search.bulkactions.types.IssueSelectType.clear.value

        if (issues && issueType && issueType !== issueSelectTypeClear) {
            args.issues = issues
            args.issue_type = issueType
        } else if (issueType && issueType === issueSelectTypeClear) {
            args.issue_type = issueType
        }

        return args
    }

    const onSubmitMerge = () => {
        const args = {}
        const merge = values.get("merge")

        if (merge) {
            args.merge = merge
        }

        return args
    }

    const onSubmitOrganization = () => {
        const args = {}
        const organizations = values.get("organizations")
        const organizationSelectType = values.get("organization_select_type")
        const organizationSelectTypeClear = DjangIO.app.search.bulkactions.types.OrganizationSelectType.clear.value

        if (organizations && organizationSelectType && organizationSelectType !== organizationSelectTypeClear) {
            args.organizations = organizations
            args.organization_select_type = organizationSelectType
        } else if (organizationSelectType && organizationSelectType === organizationSelectTypeClear) {
            args.organization_select_type = organizationSelectType
        }

        return args
    }

    const onSubmitPermission = () => {
        const args = {}
        const teams = values.get("teams")
        const teamType = values.get("team_type")

        if (teams && teamType) {
            args.teams = teams
            args.team_type = teamType
        } else if (
            (!teams || teams.size === 0) &&
            teamType === DjangIO.app.search.bulkactions.types.TeamSelectType.everyone.value
        ) {
            args.team_type = teamType
        }

        return args
    }

    const onSubmitPriority = () => {
        const args = {}
        const priority = values.get("priority")

        if (priority >= 0) {
            args.priority = priority
        }

        return args
    }

    const onSubmitStance = () => {
        const args = {}
        const stance = values.get("stance")

        if (stance >= 0) {
            args.stance = stance
        }

        return args
    }

    const onSubmitUnsuppress = () => {
        const args = {}
        const validEmail = values.get("valid_email")

        if (validEmail) {
            args.valid_email = validEmail
        }

        return args
    }

    const getArgs = () => {
        // we only want to pass the values that are explicitly needed for each BulkActionType
        // (this will prevent extraneous data from being saved in the args field of the BulkAction model)
        switch (props.bulkActionType) {
            case DjangIO.app.search.bulkactions.types.BulkActionType.alignment:
                return onSubmitAlignment()
            case DjangIO.app.search.bulkactions.types.BulkActionType.approve:
                return onSubmitApprove()
            case DjangIO.app.search.bulkactions.types.BulkActionType.archive:
                return onSubmitArchive()
            case DjangIO.app.search.bulkactions.types.BulkActionType.assign:
                return onSubmitAssignment()
            case DjangIO.app.search.bulkactions.types.BulkActionType.custom_field:
                return onSubmitCustomField()
            case DjangIO.app.search.bulkactions.types.BulkActionType.external_contact:
                return onSubmitExternalContact()
            case DjangIO.app.search.bulkactions.types.BulkActionType.issue:
                return onSubmitIssue()
            case DjangIO.app.search.bulkactions.types.BulkActionType.merge:
                return onSubmitMerge()
            case DjangIO.app.search.bulkactions.types.BulkActionType.organization:
                return onSubmitOrganization()
            case DjangIO.app.search.bulkactions.types.BulkActionType.stance:
                return onSubmitStance()
            case DjangIO.app.search.bulkactions.types.BulkActionType.permission:
                return onSubmitPermission()
            case DjangIO.app.search.bulkactions.types.BulkActionType.priority:
                return onSubmitPriority()
            case DjangIO.app.search.bulkactions.types.BulkActionType.unsuppress:
                return onSubmitUnsuppress()
            default:
                return false
        }
    }

    const args = getArgs()

    // this dispatches the action to the backend
    // first, it creates a SafedSearch,
    // then, it creates a new BulkAction tied to that SafedSearch
    // if the POST request returns a 400, then reduxForm will fire a SubmissionError
    // and invoke onSubmitFail
    await props.createBulkAction({
        dataType: props.quorumDataType,
        bulkActionType: props.bulkActionType.value,
        args,
        addedIds: props.addedIds,
        deletedIds: props.deletedIds,
        filters:
            // if we don't have any added ids
            !props.addedIds.length &&
            // and we do have some deletedIds (selectAll + unselected) or a bulkActionsCount (select all)
            (props.deletedIds.length || props.bulkActionsCount) &&
            // and we have filters that have been applied in search
            props.filters &&
            props.filters.size > 0 &&
            // pass the filters to a new SafedSearch!
            props.filters.toJS(),
    })

    return args
}

export const onSubmitSuccess = (result, dispatch, props) => {
    // unmounts BulkActionsConsole and de-selects the SearchActionsButtons button
    props.setActiveActionButton(props.uniqueFlvId, undefined)

    // a couple of Bulk Actions may modify the inlines in a ListView
    // because of this, we need to refresh the ListView every time we successfully fire a Bulk Action
    props.triggerDataRefresh(props.uniqueFlvId, DjangIO.newResourceFromDataType(props.quorumDataType))
    // clear the Bulk Action section of the store
    props.clearBulkActionsSlice(props.uniqueFlvId, props.bulkActionValidOptions)
    // clear the reduxForm section of the store
    props.reset()

    const getSwalText = () => {
        const qdt = DjangIO.app.models.QuorumDataType.by_value(props.quorumDataType)
        const qdtLabel = props.bulkActionsCount === 1 ? qdt.singular : qdt.plural

        // if the count of the queryset is larger than our hard limit,
        // we explain to the user that they need to nararow their search
        if (props.bulkActionsCount > props.bulkActionType.hard_limit) {
            return "This action was larger than the overall limit and will not be processed! Please narrow your search and try again."
        }
        // if the count of the queryset is larger than our soft limit,
        // we tell the user that the Bulk Action will be queued for processing
        if (props.bulkActionsCount > props.bulkActionType.limit) {
            return "Successfully submitted Bulk Action to be processed!"
        } else {
            // if the count of the queryset is below both of the above limits,
            // we process it immediately and tell the user that it processed successfully
            // (if the Bulk Action errors out, the POST request will fail and redux form automatically invokes onSubmitFail)
            return (
                `Successfully processed the ${props.bulkActionType.label} ` +
                `Bulk Action for ${props.bulkActionsCount.toLocaleString()} ` +
                `${qdtLabel}!`
            )
        }
    }

    // TODO: swals should live outside of shared/
    if (typeof swal !== "undefined") {
        swal({
            icon: "success",
            text: getSwalText(),
            title: "Success!",
        })
    }
}

export const onSubmitFail = (result, dispatch, error, props) => {
    let swalIcon = "error"
    let swalText = "There was a problem submitting this Bulk Action"
    let swalTitle = "Error"

    // unmounts BulkActionsConsole and de-selects the SearchActionsButtons button
    props.setActiveActionButton(props.uniqueFlvId, undefined)

    // Think of the case where a user filters by bills that have a 'Support' stance:

    // If there are 5 results in the QuerySet and the user then uses the Inline option button
    // to update all 5 stances to 'Oppose', since they used the Inline button, the frontend
    // will not refresh the ListView and the user will still be able to submit the BulkAction even
    // though there are no longer any bills in that QuerySet (all bills where stance = 'Support').

    // This is because we use frontend counts to validate if something can be submitted in the BulkActionConsole reduxForm

    // So, we should still send the standard error message
    // ("Tried to create a Bill BulkAction of type Stance on an empty selection. Please reload the page and try again.")
    // to the frontend, but also refresh the ListView to ensure that we fire a new request
    // and get the latest QuerySet so that the error cannot occur again.
    props.triggerDataRefresh(props.uniqueFlvId, DjangIO.newResourceFromDataType(props.quorumDataType))
    // clear the Bulk Action section of the store
    props.clearBulkActionsSlice(props.uniqueFlvId, props.bulkActionValidOptions)
    // clear the reduxForm section of the store
    props.reset()

    if (props.invalid) {
        swalText = Object.values(props.syncErrors).join("\n")
    }

    if (error && error.request) {
        const requestResponse = error.request.response

        if (requestResponse) {
            if (error.request.status === constants.HTTP_CONFLICT) {
                swalIcon = "success"
                swalText = requestResponse
                swalTitle = "Success!"

                // since this is basically a success, we still want to
                // refresh the ListView, hide the BulkActionConsole, etc.
                props.setActiveActionButton(props.uniqueFlvId, undefined)
                props.triggerDataRefresh(props.uniqueFlvId, DjangIO.newResourceFromDataType(props.quorumDataType))
                props.clearBulkActionsSlice(props.uniqueFlvId, props.bulkActionValidOptions)
                props.reset()
            } else if (error.request.status === 400 && error.response?.data) {
                swalText = error.response.data
            } else if (!stringContainsHTMLMarkup(requestResponse)) {
                swalText = requestResponse
            }
        }
    }

    // TODO: swals should live outside of shared/
    if (typeof swal !== "undefined") {
        swal({
            icon: swalIcon,
            title: swalTitle,
            text: swalText,
        })
    }
}

export const validate = (immutableValues, props) => {
    const errors = {}

    const validateAlignment = () => {
        const alignmentType = immutableValues.get("alignment_type")
        // If alignmentType exists, it should be an int
        if (alignmentType && !parseInt(alignmentType)) {
            errors.alignment_type = "Invalid Alignment Type Value"
        }
    }

    const validateApprove = () => {
        const approve = immutableValues.get("approve")

        if (approve !== true) {
            errors.approve = "Invalid Approve Value"
        }
    }

    const validateArchive = () => {
        const archive = immutableValues.get("archive")

        if (archive !== true) {
            errors.archive = "Invalid Archive Value"
        }
    }

    const validateAssignment = () => {
        const assignmentInputType = immutableValues.get("assignment_input_type")
        const assignmentSelectType = immutableValues.get("assignment_select_type")
        const primary = immutableValues.get("primary")
        const users = immutableValues.get("users")
        const supporters = immutableValues.get("supporters")

        if (assignmentSelectType !== DjangIO.app.search.bulkactions.types.AssignmentSelectType.clear.value) {
            // only check the user inputs if we are not clearing
            if (assignmentInputType === DjangIO.app.search.bulkactions.types.AssignmentInputType.user.value) {
                if (
                    // if we select and de-select users (without ever selecting any supporters)
                    (users && users.size === 0) ||
                    // or we have not yet made a selection of users
                    !users
                ) {
                    errors.users = "Please choose Team Member(s)"
                }

                if (primary && (!users || (users && users.size === 0))) {
                    errors.users = "Please choose a single Team Member!"
                }

                if (primary && users && users.size > 1) {
                    errors.users = "You can only set a primary assignment on a single Team Member!"
                }
            }

            // only check the supporter inputs if we are not clearing
            else if (assignmentInputType === DjangIO.app.search.bulkactions.types.AssignmentInputType.supporter.value) {
                if (
                    // if we select and de-select supporters (without ever selecting any users)
                    (supporters && supporters.size === 0) ||
                    // or we have not yet made a selection of supporters
                    !supporters
                ) {
                    errors.supporters = "Please choose Contact(s)"
                }
            }
        }
    }

    const validateCustomField = () => {
        const customFieldSlug = immutableValues.get("custom_field_slug")
        const customFieldType = immutableValues.get("custom_field_type")
        const customFieldValue = immutableValues.get(customFieldSlug)

        errors._error = "Please choose a value!" // eslint-disable-line no-underscore-dangle

        if (customFieldType === DjangIO.app.search.bulkactions.types.CustomFieldSelectType.clear.value) {
            if (customFieldSlug) {
                delete errors._error // eslint-disable-line no-underscore-dangle
            }
        } else {
            // IE 9+ compatible way to check if a js object is an Array
            const isCustomFieldValueArray = Array.isArray(customFieldValue)

            if (
                // if the custom field value in the store is an array that is greater than 0
                (isCustomFieldValueArray && customFieldValue.length) ||
                // or if the custom field value in the store is a primitive that exists (and can also be false)
                (!isCustomFieldValueArray && (customFieldValue || customFieldValue === false))
            ) {
                delete errors._error // eslint-disable-line no-underscore-dangle
            }
        }
    }

    const validateExternalContact = () => {
        const externalContact = immutableValues.get("external_contact")

        if (
            externalContact > 0 &&
            !DjangIO.app.search.bulkactions.types.ExternalContactType.items().some(
                (item) => item.value === externalContact,
            )
        ) {
            errors.external_contact = "Invalid External Contact Logger Value"
        }
    }

    const validateIssue = () => {
        const issues = immutableValues.get("issues")
        const issueType = immutableValues.get("issue_type")

        if (
            !issueType &&
            !DjangIO.app.search.bulkactions.types.IssueSelectType.items().some((item) => item.value === issueType)
        ) {
            errors.issue_type = "Please choose an Issue type!"
        }

        if (
            (!issues || issues.size === 0) &&
            issueType !== DjangIO.app.search.bulkactions.types.IssueSelectType.clear.value
        ) {
            errors.issues = "Please choose Issue(s)"
        }
    }

    const validateMerge = () => {
        const merge = immutableValues.get("merge")

        if (!merge || merge.size === 0) {
            errors.merge = `Please choose a ${
                DjangIO.app.models.QuorumDataType.by_value(props.quorumDataType).singular
            }`
        }
    }

    const validateOrganization = () => {
        const organizations = immutableValues.get("organizations")
        const organizationSelectType = immutableValues.get("organization_select_type")

        if (
            !organizationSelectType &&
            !DjangIO.app.search.bulkactions.types.OrganizationSelectType.items().some(
                (item) => item.value === organizationSelectType,
            )
        ) {
            errors.organization_select_type = "Please choose an Organization type!"
        }

        if (
            (!organizations || typeof organizations !== "object" || organizations.size === 0) &&
            organizationSelectType !== DjangIO.app.search.bulkactions.types.OrganizationSelectType.clear.value
        ) {
            errors.organization = "Please choose an Organization"
        }
    }

    const validatePermission = () => {
        const teams = immutableValues.get("teams")
        const teamType = immutableValues.get("team_type")

        if (
            teams &&
            teams.size &&
            !DjangIO.app.search.bulkactions.types.TeamSelectType.items().some((item) => item.value === teamType)
        ) {
            errors.team_type = "Please choose a Team type!"
        }

        if (
            teamType !== DjangIO.app.search.bulkactions.types.TeamSelectType.everyone.value &&
            (!teams || teams.size === 0)
        ) {
            errors.teams = "Please choose Team(s)"
        }
    }

    const validatePriority = () => {
        const priority = immutableValues.get("priority")

        if (
            priority >= 0 &&
            !DjangIO.app.userdata.types.LegislativePriorityType.items().some((item) => item.value === priority)
        ) {
            errors.priority = "Invalid Priority Value"
        }
    }

    const validateStance = () => {
        const stance = immutableValues.get("stance")

        if (
            stance >= 0 &&
            !DjangIO.app.userdata.types.LegislativeStanceType.items().some((item) => item.value === stance)
        ) {
            errors.stance = "Invalid Stance Value"
        }
    }

    const validateUnsuppress = () => {
        const validEmail = immutableValues.get("valid_email")

        if (validEmail !== true) {
            errors.valid_email = "Invalid Unsuppress Value"
        }
    }

    // we only want to pass the values that are explicitly needed for each BulkActionType
    // (this will prevent extraneous data from being saved in the args field of the BulkAction model)
    switch (props.bulkActionType) {
        case DjangIO.app.search.bulkactions.types.BulkActionType.alignment:
            validateAlignment()
            break
        case DjangIO.app.search.bulkactions.types.BulkActionType.approve:
            validateApprove()
            break
        case DjangIO.app.search.bulkactions.types.BulkActionType.archive:
            validateArchive()
            break
        case DjangIO.app.search.bulkactions.types.BulkActionType.assign:
            validateAssignment()
            break
        case DjangIO.app.search.bulkactions.types.BulkActionType.custom_field:
            validateCustomField()
            break
        case DjangIO.app.search.bulkactions.types.BulkActionType.external_contact:
            validateExternalContact()
            break
        case DjangIO.app.search.bulkactions.types.BulkActionType.issue:
            validateIssue()
            break
        case DjangIO.app.search.bulkactions.types.BulkActionType.merge:
            validateMerge()
            break
        case DjangIO.app.search.bulkactions.types.BulkActionType.organization:
            validateOrganization()
            break
        case DjangIO.app.search.bulkactions.types.BulkActionType.stance:
            validateStance()
            break
        case DjangIO.app.search.bulkactions.types.BulkActionType.permission:
            validatePermission()
            break
        case DjangIO.app.search.bulkactions.types.BulkActionType.priority:
            validatePriority()
            break
        case DjangIO.app.search.bulkactions.types.BulkActionType.unsuppress:
            validateUnsuppress()
            break
        default:
            break
    }

    return errors
}
